import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      leftToSpend: '[data-left-to-spend]',
      leftToGift: '[data-left-to-gift]',
      shippingMilestone: '[data-milestone="shipping"]',
      giftMilestone: '[data-milestone="gift"]',
    };
    const rate = Number(window.Shopify?.currency?.rate || 1) || 1;
    this.shippingGoal = Number(this.dataset.minimumAmount) * rate || 0;
    this.giftGoal = Number(this.dataset.giftAmount) * rate || 0;
    this.giftEnabled = this.dataset.giftEnabled === 'true' && this.giftGoal > this.shippingGoal;
    this.maxGoal = this.giftEnabled ? this.giftGoal : this.shippingGoal;
    this.goal = this.maxGoal;
    this.progress = this.querySelector('progress-bar');
    this.progressFill = this.querySelector('.progress-bar__fill');
    this.shippingMilestone = this.querySelector(this.selectors.shippingMilestone);
    this.giftMilestone = this.querySelector(this.selectors.giftMilestone);
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.currency = this.dataset.currency || /** @type {any} */ (window).theme?.currency || 'USD';
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.onLegacyCartUpdate = this.onLegacyCartUpdate.bind(this);
  }

  connectedCallback() {
    this.updateShippingGoal(Number(this.dataset.cartTotal));
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.addEventListener('cart:update', this.onLegacyCartUpdate);
    document.addEventListener('cart:updated', this.onLegacyCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.removeEventListener('cart:update', this.onLegacyCartUpdate);
    document.removeEventListener('cart:updated', this.onLegacyCartUpdate);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  onCartUpdate(event) {
    event.promise
      ?.then(({ cart, detail }) => {
        if (this.#applyFromSectionMarkup(detail?.sections)) return;

        const cents = this.#centsFromCartSummary(cart);
        if (typeof cents === 'number') {
          this.dataset.cartTotal = String(cents);
          this.updateShippingGoal(cents);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[free-shipping-goal] Cart update promise rejected:', error);
        }
      });
  }

  /** @param {Event} event */
  onLegacyCartUpdate(event) {
    const cartEvent = /** @type {CustomEvent<any>} */ (event);
    const cart = cartEvent.detail?.resource ?? cartEvent.detail?.cart;
    const nextAmount = cart?.items_subtotal_price ?? cart?.total_price;
    if (typeof nextAmount === 'number') {
      this.dataset.cartTotal = String(nextAmount);
      this.updateShippingGoal(nextAmount);
      return;
    }

    if (this.#applyFromSectionMarkup(cartEvent.detail?.data?.sections ?? cartEvent.detail?.sections)) {
      return;
    }

    requestAnimationFrame(() => {
      this.updateShippingGoal(Number(this.dataset.cartTotal || 0));
    });
  }

  /**
   * @param {Record<string, string> | null | undefined} sections
   * @returns {boolean}
   */
  #applyFromSectionMarkup(sections) {
    const cartItemsComponent = /** @type {HTMLElement | null} */ (this.closest('cart-items-component'));
    const sectionId = cartItemsComponent?.dataset.sectionId;
    const sectionMarkup = sectionId && sections ? sections[sectionId] : null;
    if (!sectionMarkup) return false;

    const newSectionHTML = new DOMParser().parseFromString(sectionMarkup, 'text/html');
    const nextGoalElement =
      newSectionHTML.querySelector(`free-shipping-goal[data-minimum-amount="${this.dataset.minimumAmount}"]`) ||
      newSectionHTML.querySelector('free-shipping-goal');
    const nextCartTotal = Number(nextGoalElement?.getAttribute('data-cart-total'));
    if (!Number.isFinite(nextCartTotal)) return false;

    this.dataset.cartTotal = String(nextCartTotal);
    this.updateShippingGoal(nextCartTotal);
    return true;
  }

  /**
   * @param {any} cart
   * @returns {number | null}
   */
  #centsFromCartSummary(cart) {
    if (!cart) return null;

    if (typeof cart.total_price === 'number') return cart.total_price;
    if (typeof cart.items_subtotal_price === 'number') return cart.items_subtotal_price;

    const amount = cart?.cost?.totalAmount?.amount;
    if (amount == null) return null;

    const parsed = typeof amount === 'number' ? amount : parseFloat(String(amount));
    if (!Number.isFinite(parsed)) return null;

    return Math.round(parsed * 100);
  }

  /**
   * @param {number} remainingMajor
   * @param {string} selector
   */
  #setRemaining(remainingMajor, selector) {
    const leftToSpendElement = this.querySelector(selector);
    if (!leftToSpendElement) return;
    leftToSpendElement.innerHTML = formatMoney(remainingMajor * 100, this.moneyFormat, this.currency);
  }

  /** @param {number} amount */
  updateShippingGoal(amount) {
    if (!this.maxGoal || !(this.progress instanceof HTMLElement)) return;
    if (!(this.progressFill instanceof HTMLElement)) {
      this.progressFill = this.querySelector('.progress-bar__fill');
    }

    if (amount > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    this.cartTotal = amount / 100;
    const shippingDone = this.cartTotal >= this.shippingGoal;
    const giftDone = this.giftEnabled && this.cartTotal >= this.giftGoal;
    const complete = this.giftEnabled ? giftDone : shippingDone;

    let stage = 'shipping';
    if (complete) {
      stage = 'complete';
    } else if (shippingDone && this.giftEnabled) {
      stage = 'gift';
    }

    this.dataset.stage = stage;
    this.classList.toggle('free-shipping-goal--shipping-done', shippingDone);
    this.classList.toggle('free-shipping-goal--gift-done', Boolean(giftDone));
    this.classList.toggle('free-shipping-goal--done', complete);

    this.shippingMilestone?.classList.toggle('is-reached', shippingDone);
    this.giftMilestone?.classList.toggle('is-reached', Boolean(giftDone));

    this.percent = (this.cartTotal * 100) / this.maxGoal;
    if (!Number.isFinite(this.percent)) this.percent = 0;
    if (this.percent >= 100) this.percent = 100;
    if (this.percent < 0) this.percent = 0;

    if (this.progressFill instanceof HTMLElement) {
      this.progressFill.style.width = `${this.percent}%`;
    }

    if (!shippingDone) {
      this.#setRemaining(this.shippingGoal - this.cartTotal, this.selectors.leftToSpend);
    } else if (this.giftEnabled && !giftDone) {
      this.#setRemaining(this.giftGoal - this.cartTotal, this.selectors.leftToGift);
    }

    this.progress.dataset.value = String(this.cartTotal);
    this.progress.dataset.max = String(this.maxGoal);
  }
}

if (!customElements.get('free-shipping-goal')) {
  customElements.define('free-shipping-goal', FreeShippingGoal);
}
